import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { invalidateTickets } from '../../lib/tickets-invalidation';
import { unwrapArray } from '../../lib/api-envelope';
import { fmtCurrency, parseMoney } from '../../lib/display-labels';
import SortTh, { sortCompare, makeToggleSort } from '../SortTh';
import { FileText, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { formatInstantDay } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';

interface DeletedTicket {
  id: string;
  ticketNumber?: string;
  customer: string;
  jobNumber?: string;
  status: string;
  /** null when the server had no total — rendered '—', never $0.00. */
  total: number | null;
  deletedAt: string;
  deletedBy: string | null;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  field_approved: 'Field Approved',
  office_approved: 'Office Approved',
  customer_approved: 'Customer Approved',
  customer_rejected: 'Customer Rejected',
  rejected: 'Rejected',
  invoiced: 'Invoiced',
  archived: 'Archived',
  active: 'Draft',
  submitted: 'Draft',
  approved: 'Office Approved',
};

function mapDeletedTicket(t: any): DeletedTicket {
  return {
    id: t.id,
    ticketNumber: t.ticket_number || t.ticketNumber,
    customer: t.customer_name || t.customerName || t.customer || 'Unknown',
    jobNumber: t.job_number || t.jobNumber,
    status: t.status || 'draft',
    total: parseMoney(t.total_amount ?? t.totalAmount ?? t.total_price ?? t.totalPrice ?? t.total),
    deletedAt: t.deleted_at || t.deletedAt || '',
    deletedBy: t.deleted_by_name || t.deletedByName || t.deleted_by || t.deletedBy || null,
  };
}

/** deleted_at is an instant; the day it shows is the TENANT's day. */
function fmtDate(d: any, timeZone: string | null) {
  return formatInstantDay(
    typeof d === 'string' ? d : null,
    timeZone,
    { month: 'short', day: 'numeric', year: 'numeric' },
    'en-US',
  ) ?? '\u2014';
}

interface DeletedTicketsTabProps {
  currency: string;
  search: string;
}

export default function DeletedTicketsTab({ currency, search }: DeletedTicketsTabProps) {
  const { user } = useAuth();
  const timeZone = useTenantTimeZone();
  const queryClient = useQueryClient();
  const canRestore = user?.role === 'admin' || user?.role === 'office_staff';

  const [sortBy, setSortBy] = useState('deletedAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [confirmTicket, setConfirmTicket] = useState<DeletedTicket | null>(null);

  const { data: rawData, isLoading } = useQuery({
    queryKey: ['tickets', 'deleted'],
    queryFn: async () => {
      const res = await api.tickets.listDeleted();
      return unwrapArray<any>(res.data);
    },
    retry: false,
  });

  const tickets: DeletedTicket[] = useMemo(
    () => (rawData || []).map((t: any) => mapDeletedTicket(t)),
    [rawData],
  );

  const filtered = useMemo(() => {
    if (!search) return tickets;
    const s = search.toLowerCase();
    return tickets.filter((t) => {
      const num = (t.ticketNumber || t.id || '').toLowerCase();
      const cust = (t.customer || '').toLowerCase();
      const jobNum = (t.jobNumber || '').toLowerCase();
      return num.includes(s) || cust.includes(s) || jobNum.includes(s);
    });
  }, [tickets, search]);

  const toggleSort = makeToggleSort(setSortBy, setSortDir, sortBy);
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = (a as any)[sortBy];
      const bVal = (b as any)[sortBy];
      return sortCompare(aVal, bVal, sortDir);
    });
  }, [filtered, sortBy, sortDir]);

  const restoreMutation = useMutation({
    mutationFn: (id: string) => api.tickets.restore(id),
    onSuccess: () => {
      invalidateTickets(queryClient);
      toast.success('Ticket restored');
      setConfirmTicket(null);
    },
    onError: () => {
      toast.error('Failed to restore ticket');
    },
  });

  const handleRestore = (ticket: DeletedTicket) => {
    if (ticket.status !== 'draft') {
      setConfirmTicket(ticket);
    } else {
      restoreMutation.mutate(ticket.id);
    }
  };

  // Loading
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 size={24} className="animate-spin text-fieldiq-accent" />
      </div>
    );
  }

  // Empty state
  if (sorted.length === 0) {
    return (
      <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-8 text-center">
        <FileText size={40} className="mx-auto text-fieldiq-muted mb-3" />
        <p className="text-white font-medium">No tickets found</p>
        <p className="text-fieldiq-muted text-sm mt-1">No deleted tickets</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden md:block bg-fieldiq-card rounded-xl border border-fieldiq-border overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-fieldiq-border">
              <SortTh label="Ticket #" field="ticketNumber" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="w-[100px]" />
              <SortTh label="Client" field="customer" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
              <SortTh label="Job #" field="jobNumber" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
              <SortTh label="Status" field="status" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
              <SortTh label="Total" field="total" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} className="!text-right" />
              <SortTh label="Deleted" field="deletedAt" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
              <SortTh label="Deleted By" field="deletedBy" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} />
              {canRestore && (
                <th className="text-xs uppercase tracking-wider font-bold text-fieldiq-muted px-4 py-3 w-[90px]"></th>
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.map((ticket) => (
              <tr
                key={ticket.id}
                className="border-b border-fieldiq-border last:border-b-0 hover:bg-fieldiq-dark/50 transition-colors"
              >
                <td className="px-4 py-2.5 text-sm font-semibold text-fieldiq-accent whitespace-nowrap tabular-nums">
                  {ticket.ticketNumber || ticket.id.slice(0, 8)}
                </td>
                <td className="px-4 py-2.5 text-sm font-semibold text-white">{ticket.customer}</td>
                <td className="px-4 py-2.5 text-sm text-fieldiq-text">{ticket.jobNumber || '\u2014'}</td>
                <td className="px-4 py-2.5 text-sm font-medium text-white">
                  {STATUS_LABELS[ticket.status] || ticket.status}
                </td>
                <td className="px-4 py-2.5 text-sm font-bold text-green-400 text-right">
                  {fmtCurrency(ticket.total, currency)}
                </td>
                <td className="px-4 py-2.5 text-sm text-fieldiq-text">{fmtDate(ticket.deletedAt, timeZone)}</td>
                <td className="px-4 py-2.5 text-sm text-fieldiq-text">{ticket.deletedBy || '\u2014'}</td>
                {canRestore && (
                  <td className="px-4 py-2.5">
                    <button
                      onClick={() => handleRestore(ticket)}
                      disabled={restoreMutation.isPending}
                      className="flex items-center gap-1.5 text-xs font-semibold text-fieldiq-accent hover:text-white transition-colors disabled:opacity-50"
                    >
                      <RotateCcw size={14} />
                      Restore
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-2.5">
        {sorted.map((ticket) => (
          <div
            key={ticket.id}
            className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4"
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-fieldiq-accent">
                {ticket.ticketNumber || ticket.id.slice(0, 8)}
              </span>
              <span className="text-sm font-medium text-white">
                {STATUS_LABELS[ticket.status] || ticket.status}
              </span>
            </div>
            <p className="text-sm font-semibold text-white truncate">{ticket.customer}</p>
            <p className="text-sm text-fieldiq-text mt-0.5">
              {ticket.jobNumber ? `Job #${ticket.jobNumber}` : '\u2014'}
            </p>
            <div className="flex items-center justify-between mt-2">
              <div className="text-xs text-fieldiq-muted">
                Deleted {fmtDate(ticket.deletedAt, timeZone)} {ticket.deletedBy ? `by ${ticket.deletedBy}` : ''}
              </div>
              <span className="text-sm font-bold text-green-400">
                {fmtCurrency(ticket.total, currency)}
              </span>
            </div>
            {canRestore && (
              <button
                onClick={() => handleRestore(ticket)}
                disabled={restoreMutation.isPending}
                className="mt-3 flex items-center gap-1.5 text-xs font-semibold text-fieldiq-accent hover:text-white transition-colors disabled:opacity-50"
              >
                <RotateCcw size={14} />
                Restore
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Confirm dialog for non-draft tickets */}
      {confirmTicket && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 max-w-md w-full">
            <h3 className="text-white font-bold text-lg mb-2">Restore ticket?</h3>
            <p className="text-fieldiq-text text-sm mb-1">
              This ticket has status <span className="font-semibold text-white">{STATUS_LABELS[confirmTicket.status] || confirmTicket.status}</span>.
            </p>
            {confirmTicket.status === 'customer_approved' && (
              <p className="text-amber-400 text-sm mb-4">
                Restoring it will make it invoice-eligible again.
              </p>
            )}
            {confirmTicket.status !== 'customer_approved' && (
              <p className="text-fieldiq-muted text-sm mb-4">
                It will be restored to its original status.
              </p>
            )}
            <div className="flex items-center gap-3 justify-end">
              <button
                onClick={() => setConfirmTicket(null)}
                className="px-4 py-2 text-sm font-semibold text-fieldiq-text hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => restoreMutation.mutate(confirmTicket.id)}
                disabled={restoreMutation.isPending}
                className="px-4 py-2 text-sm font-semibold bg-fieldiq-accent text-fieldiq-dark rounded-lg hover:bg-fieldiq-accent/90 transition-colors disabled:opacity-50"
              >
                {restoreMutation.isPending ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/**
 * The deleted-ticket count for the chip, as THREE states rather than a number.
 *
 * It used to return `data?.length ?? 0`, and that `?? 0` is the whole reason
 * this returns an object now. A refused or failed read arrives as `undefined`
 * data; coalescing it to `0` told the reader "there are no deleted tickets",
 * which is a claim about the data made out of a failure to fetch it. It is the
 * same shape the rest of this screen was repaired for — an empty answer and no
 * answer must not share a spelling.
 *
 * `count` is `undefined` for BOTH "in flight" and "did not arrive", so it
 * cannot be the only thing a caller reads. `settled` is what separates them:
 *
 *   in flight   settled false, count undefined  -> claim no number at all
 *   loaded      settled true,  count a number   -> show it; a real 0 is a 0
 *   failed      settled true,  count undefined  -> show the absent marker
 *
 * A caller that destructures only `count` gets the in-flight and failed cases
 * rendering identically, which is a narrower bug than the one this replaces but
 * still a bug — hence both fields, and hence the chip reads both.
 */
export function useDeletedTicketsCount(): { count: number | undefined; settled: boolean } {
  const { data, isPending, isError } = useQuery({
    queryKey: ['tickets', 'deleted'],
    queryFn: async () => {
      const res = await api.tickets.listDeleted();
      return unwrapArray<any>(res.data);
    },
    // A refusal is an answer, not a flake. Retrying a 403 spends the reader's
    // time to be told the same thing, and leaves the chip in flight while it
    // does — which is why this stays off and `settled` can be trusted.
    retry: false,
  });
  // `isError` rather than `data === undefined`: a query that resolved to an
  // empty list has `data === []`, and the distinction between that and a
  // refusal is the entire point of this hook.
  if (isError) return { count: undefined, settled: true };
  if (isPending) return { count: undefined, settled: false };
  return { count: data?.length, settled: true };
}
